/* eslint-disable complexity */
import React from 'react';
import clsx from 'clsx';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { LOGIN_ROUTE, MyProfile, REGISTER_BY_PRODUCT_ROUTE, REGISTER_ROUTE } from '@futrli/auth';
import { LOGOUT_ROUTE } from '@futrli/auth/core/constants';
import {
  matchConsolidatedForecastingRoutePath,
  matchPredictForecastingPath,
  matchPredictNonFinancialDriversPagePath,
  matchPredictPerformancePagePath,
  matchPredictPredictionsPagePath,
  matchPredictSettingsPath,
  ROUTES
} from '@futrli/shared-services';
import { matchWorkspaceRoutePath } from '@futrli/predict-next/workspaces/utils/route-utils';
import { PrimaryNavContainer } from '@futrli/predict-next/nav/primary/containers/primary-nav-container/primary-nav-container';
import { SyncContextProvider, useSxpContext } from '@futrli/predict';
import { WorkspacesEntryGates } from '@futrli/predict-next/workspaces/entry-gates/containers/workspaces-entry-gates/workspaces-entry-gates';

import { AppNavbar } from './app-navbar';
import { AppNavbarNotifications } from './app-navbar-notifications';
import { AppNavbarSecondary } from './app-navbar-secondary/app-navbar-secondary';
import { Logout } from '../logout';
import { NotFound } from '../../errors/components/not-found';
import { OnboardingManager } from '../../onboarding/components/onboarding-manager';
import { shouldHideNav } from '../../utils/routes-utils';
import { WorkspacesHybridRouter } from '../../workspaces/routing/workspaces-hybrid-router';

import styles from '../../core/common.scss';

export const AppManagement = (): JSX.Element => {
  const flags = useFlags();
  const { displayGlobalNav } = useSxpContext();
  const location = useLocation();

  const isFullScreen =
    shouldHideNav(location) ||
    matchPredictForecastingPath(location.pathname) ||
    matchConsolidatedForecastingRoutePath(location.pathname) ||
    matchPredictPerformancePagePath(location.pathname) ||
    matchPredictSettingsPath(location.pathname) ||
    matchPredictPredictionsPagePath(location.pathname) ||
    matchPredictNonFinancialDriversPagePath(location.pathname) ||
    location.pathname.indexOf(ROUTES.ONBOARDING_ROUTE) !== -1 ||
    matchWorkspaceRoutePath(location.pathname, true);

  const isNoPadding =
    location.pathname.indexOf('reporting') !== -1 ||
    location.pathname.indexOf('report-branding') !== -1 ||
    location.pathname.indexOf(ROUTES.DASHBOARDS) !== -1 ||
    location.pathname.indexOf(ROUTES.HOME) !== -1;

  return (
    <SyncContextProvider>
      <React.Fragment>
        {!displayGlobalNav && (flags.primaryNavRefactor ? <PrimaryNavContainer /> : <AppNavbar />)}
        <AppNavbarSecondary displayGlobalNav={displayGlobalNav} />
        <AppNavbarNotifications />

        <main
          className={clsx('wide-screen', {
            [styles.fullscreen]: isFullScreen,
            [styles.noPadding]: isNoPadding
          })}
        >
          <Routes>
            <Route element={<WorkspacesEntryGates />}>
              <Route path={ROUTES.ROOT_ROUTE} />
              <Route path={`${ROUTES.WORKSPACES_ROUTE}/*`} element={<WorkspacesHybridRouter />} />
            </Route>

            <Route path={ROUTES.MY_PROFILE} element={<MyProfile />} />

            <Route path={`${ROUTES.ONBOARDING_ROUTE}/*`} element={<OnboardingManager />} />

            <Route path={REGISTER_ROUTE} element={<Navigate to={ROUTES.ROOT_ROUTE} replace />} />
            <Route path={REGISTER_BY_PRODUCT_ROUTE} element={<Navigate to={ROUTES.ROOT_ROUTE} replace />} />
            <Route path={LOGIN_ROUTE} element={<Navigate to={ROUTES.ROOT_ROUTE} replace />} />
            <Route path={LOGOUT_ROUTE} element={<Logout />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </React.Fragment>
    </SyncContextProvider>
  );
};
